import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Anchor,
  Box,
  Group,
  Loader,
  Paper,
  Select,
  Text,
} from '@mantine/core'
import { IconAlertCircle, IconExternalLink } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import {
  getGenieEmbedUrl,
  getGenieSpaceUrl,
  isGenieSpaceAvailable,
  listGenieSpaces,
  type GenieSpace,
} from '../../api/genie'

function spaceLabel(space: GenieSpace): string {
  return space.display_name || space.title || space.space_id
}

/**
 * Chat with Data backed by an embedded Databricks Genie Agent (formerly Genie
 * space) rather than the Genie Conversation API.
 *
 * Databricks serves the agent cross-origin, so the browser gives us no way to
 * tell whether the frame actually rendered or was refused: `onLoad` fires
 * either way and the document cannot be inspected. Rather than guess at a
 * failure state, the page always offers a direct link out to Databricks so a
 * user facing a blank frame is never stuck.
 */
export function EmbeddedGenieChat() {
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [frameLoaded, setFrameLoaded] = useState(false)

  const {
    data: spaces = [],
    isLoading,
    isError,
    error,
  } = useQuery(['genie', 'spaces'], listGenieSpaces, { retry: 1 })

  const availableSpaces = useMemo(
    () => spaces.filter(isGenieSpaceAvailable),
    [spaces]
  )

  // Prefer the configured default space, falling back to the first the user
  // can see -- mirrors the space selection on the Conversation API page.
  useEffect(() => {
    const preferred = import.meta.env.VITE_GENIE_SPACE_ID
    const match = preferred
      ? availableSpaces.find((space) => space.space_id === preferred)
      : undefined
    setSelectedSpaceId(match?.space_id || availableSpaces[0]?.space_id || null)
  }, [availableSpaces])

  // Reset the loading overlay whenever the user switches agents.
  useEffect(() => {
    setFrameLoaded(false)
  }, [selectedSpaceId])

  const embedUrl = selectedSpaceId ? getGenieEmbedUrl(selectedSpaceId) : null
  const spaceUrl = selectedSpaceId ? getGenieSpaceUrl(selectedSpaceId) : null

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 140px)',
      }}
    >
      <Group justify="space-between" mb="md" align="flex-end">
        <Box>
          <Text size="xl" fw={700}>
            Chat with your data
          </Text>
          <Text size="sm" c="dimmed">
            Ask questions about Prism data in the Databricks Genie experience,
            embedded here and authenticated with your Databricks account.
          </Text>
        </Box>
        <Group gap="md" align="flex-end">
          {availableSpaces.length > 0 && (
            <Select
              label="Genie agent"
              w={260}
              data={availableSpaces.map((space) => ({
                value: space.space_id,
                label: spaceLabel(space),
              }))}
              value={selectedSpaceId}
              onChange={setSelectedSpaceId}
              allowDeselect={false}
              searchable={availableSpaces.length > 8}
            />
          )}
          {spaceUrl && (
            <Anchor
              href={spaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              pb={8}
            >
              <Group gap={4}>
                <Text size="sm">Open in Databricks</Text>
                <IconExternalLink size={14} />
              </Group>
            </Anchor>
          )}
        </Group>
      </Group>

      {isLoading && (
        <Box
          style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}
        >
          <Loader color="prism" />
        </Box>
      )}

      {isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error instanceof Error
            ? error.message
            : 'Failed to load Genie agents.'}
        </Alert>
      )}

      {!isLoading && !isError && availableSpaces.length === 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          title="No Genie agents available"
        >
          You do not have access to any Genie agents in this workspace, or none
          have been created yet. Ask a workspace admin to grant access to the
          agent and its underlying data.
        </Alert>
      )}

      {!isLoading && !isError && selectedSpaceId && !embedUrl && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          title="Databricks host not configured"
        >
          Set <code>VITE_DATABRICKS_HOST</code> so the Genie agent URL can be
          resolved.
        </Alert>
      )}

      {embedUrl && (
        <>
          <Paper
            withBorder
            radius="md"
            style={{ flex: 1, overflow: 'hidden', position: 'relative' }}
          >
            {!frameLoaded && (
              <Box
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader color="prism" />
              </Box>
            )}
            <iframe
              key={embedUrl}
              src={embedUrl}
              title="Databricks Genie"
              onLoad={() => setFrameLoaded(true)}
              allow="clipboard-write"
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          </Paper>
          <Text size="xs" c="dimmed" mt="xs">
            If the panel stays blank, a workspace admin may need to add this
            site to the allowed embedding surfaces in Databricks, or your
            browser may be blocking third-party cookies. Use Open in Databricks
            in the meantime.
          </Text>
        </>
      )}
    </Box>
  )
}
